import React from 'react';
import { useSelector } from 'react-redux';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import { GridColDef, useGridApiRef } from '@mui/x-data-grid-pro';
import RentleDataGrid from 'components/datagrid/RentleDataGrid';
import { PaymentWithTransactions } from '../types';
import { getPricingString, notUndefined } from 'common/utils/common';
import AccountingToolbar from './AccountingToolbar';
import { useTranslation } from 'services/localization';
import moment from 'moment-timezone';

interface ItemExportRow {
	id: string;
	receipt_number: number;
	booking_date: string;
	booking_date_utc: string;
	sales_date: string;
	sales_date_utc: string;
	type: 'PAYMENT' | 'REFUND';
	name: string;
	custom_groups: string[];
	payment_id: string;
	store: string;
	transaction_ids: string;
	excl_tax: string;
	tax: string;
	tax_percent: string;
	total: string;
}

interface ItemReportTableColDef extends GridColDef {
	field: keyof ItemExportRow;
}

const columnFields: (keyof ItemExportRow)[] = [
	'receipt_number',
	'booking_date',
	'booking_date_utc',
	'sales_date',
	'sales_date_utc',
	'store',
	'type',
	'name',
	'custom_groups',
	'payment_id',
	'transaction_ids',
	'excl_tax',
	'tax',
	'tax_percent',
	'total',
];

const columns: ItemReportTableColDef[] = columnFields.map((field) => ({ field }));

interface Props {
	data: PaymentWithTransactions[];
	getExportFileName: (reportName: string) => string;
}

const ItemReportTable = (props: Props) => {
	const { t } = useTranslation();
	const apiRef = useGridApiRef();
	const customAccountingGroups = useSelector(ShopSelectors.customAccountingGroups);
	const shopLocationsById = useSelector(ShopSelectors.allShopLocationsById);
	const stockProductIdAccountingGroupMap = useSelector(
		StockSelectors.stockProductIdAccountingGroupMap,
	);

	const getItemBreakdownRows = (data: PaymentWithTransactions[]) => {
		const rows = data.flatMap(({ payment, transactions }) => {
			const transactionIds = transactions.map((t) => t.id);
			const receiptNumber = payment.receiptNumber;
			const bookingDateUtc = payment.timestamp;
			const bookingDateLocal = moment(payment.timestamp).format();
			const salesDateUtc = payment.usageDate;
			const salesDateLocal = moment(payment.usageDate).format();
			const type =
				payment.paymentAction === 'CAPTURE' || payment.paymentAction === 'PAYMENT'
					? 'PAYMENT'
					: payment.paymentAction === 'REFUND'
					? 'REFUND'
					: '';
			if (!type) {
				throw new Error('Error fetching report');
			}
			const paymentId = payment.id;
			const itemRows: ItemExportRow[] = payment.items.flatMap((item, index) => {
				const name = item.name;
				const itemStockProductId = item.stockProductId;
				const accountingGroupIds = itemStockProductId
					? stockProductIdAccountingGroupMap[itemStockProductId] ?? []
					: [];
				const accountingGroups = accountingGroupIds
					.map((id) => customAccountingGroups.find((g) => g.id === id)?.name)
					.filter(notUndefined);
				const multiplier = type === 'REFUND' ? -1 : 1;
				const total = item.pricing.total * multiplier;
				const taxPercent = item.pricing.taxLines.map((l) => l.rate * 100).join(', ');
				const tax = item.pricing.totalTaxes * multiplier;
				const taxExcluded = total - tax;
				return {
					id: `${paymentId}_${item.name}_${index}`,
					receipt_number: receiptNumber,
					booking_date: bookingDateLocal,
					booking_date_utc: bookingDateUtc,
					sales_date: salesDateLocal,
					sales_date_utc: salesDateUtc,
					type,
					name,
					store: shopLocationsById[payment.locationId ?? '']?.name ?? '',
					custom_groups: accountingGroups,
					payment_id: paymentId,
					transaction_ids: transactionIds.join(', '),
					excl_tax: getPricingString(taxExcluded),
					tax: getPricingString(tax),
					tax_percent: taxPercent,
					total: getPricingString(total),
				};
			});
			return itemRows;
		});
		return rows;
	};

	return (
		<RentleDataGrid
			apiRef={apiRef}
			rows={getItemBreakdownRows(props.data)}
			columns={columns}
			density="compact"
			defaultSortModel={[
				{
					field: 'receipt_number',
					sort: 'asc',
				},
			]}
			components={{
				Toolbar: AccountingToolbar,
			}}
			componentsProps={{
				toolbar: {
					tableName: t('reports.accounting.lineItems', 'Line items'),
					exportHeaders: [],
					exportFileName: props.getExportFileName('items'),
				},
			}}
			hideFooter
			sx={{ '.MuiDataGrid-main': { display: 'none' } }}
		/>
	);
};

export default ItemReportTable;
